import { GenderE } from "../components/Gender/Gender";
import { GraphMode } from "../components/MacroNutrient/MacroNutrients";
import { UserTypeE } from "../components/UserType/UserType";
import { Details, HeightUnits, WeightUnits } from "./Details";
import { Activity, Diet, Goals, ReachGoal, Workout } from "./Goals";
import { Macronutrient } from "./MacroNutrient";
import { Util } from "./Util";

export interface NutricalQuestion {
	title: string;
	subTitle: string;
	order: number;
}

export class Nutrical {
	userType: UserTypeE = UserTypeE.MySelf;
	detail: Details;
	goal: Goals;

	// Calculation Parameters
	rmr = 0; //resting metabolic rate.
	rmrActivity = 0; // resting metabolic rate after activity (TDEE)
	rmrActivityGoal = 0; // TDEE adjusted to body goal

	caloriesProtein = 0;
	caloriesFat = 0;
	caloriesCarb = 0;
	caloriesVeg = 0;

	gramsProtein = 0;
	gramsFat = 0;
	gramsCarb = 0;
	gramsVeg = 0;

	gramsPerMealProtein = 0;
	gramsPerMealFat = 0;
	gramsPerMealCarb = 0;
	gramsPerMealVeg = 0;

	palmsProtein = 0;
	fistVegies = 0;
	cuppedHandCarb = 0;
	thumbsFat = 0;

	numberOfQuestions = 14;
	constructor() {
		this.userType = UserTypeE.MySelf;
		this.detail = new Details();
		this.goal = new Goals();
	}

	getQuestion(questionNumber: number): NutricalQuestion {
		switch (questionNumber) {
			case 0:
				return {
					title: "Is this for yourself, or for a client?",
					subTitle: "",
					order: 0,
				};
			case 1:
				return {
					title: `What's ${this.userType === UserTypeE.Client ? "their" : "your"} age?`,
					subTitle: "Okay, first question.",
					order: 1,
				};
			case 2:
				return {
					title: `What's ${this.userType === UserTypeE.Client ? "their" : "your"} biological sex?`,
					subTitle: "Very good. Let's keep moving.",
					order: 2,
				};
			case 3:
				return {
					title: `How tall are ${this.userType === UserTypeE.Client ? "they" : "you"}?`,
					subTitle: "Got it. Next question.",
					order: 3,
				};
			case 4:
				return {
					title: `How much do ${this.userType === UserTypeE.Client ? "they" : "you"} weigh right now?`,
					subTitle: "Great. Last personal detail.",
					order: 4,
				};
			case 5:
				return {
					title: `What's ${this.userType === UserTypeE.Client ? "their" : "your"} goal?`,
					subTitle: `Now tell us what ${this.userType === UserTypeE.Client ? "they" : "you"} want to achieve.`,
					order: 5,
				};
			case 6:
				return {
					title: `How much do ${this.userType === UserTypeE.Client ? "they" : "you"} want to weigh?`,
					subTitle: "Let's get really specific on this.",
					order: 6,
				};
			case 7:
				return {
					title: `What's ${this.userType === UserTypeE.Client ? "their" : "your"} preferred eating style?`,
					subTitle: "Almost there. Time to talk food.",
					order: 7,
				};
			case 8:
				return {
					title:
						`Do ${this.userType === UserTypeE.Client ? "they" : "you"} want to use ${this.goal.getEatingStyle()}'s standard macronutrient ratios, or customize?`,
					subTitle: "Tell us how to run the numbers.",
					order: 8,
				};
			case 9:
				return {
					title: `Customize ${this.userType === UserTypeE.Client ? "their" : "your"} macronutrient ratios.`,
					subTitle: `Go ahead: Make ${this.userType === UserTypeE.Client ? "their" : "your"} adjustments.`,
					order: 9,
				};
			case 10:
				return {
					title: `How many meals do ${this.userType === UserTypeE.Client ? "they" : "you"} like to eat each day?`,
					subTitle: "Just a couple of more questions.",
					order: 10,
				};
			case 11:
				return {
					title: `Describe how active ${this.userType === UserTypeE.Client ? "they" : "you"} are each day.`,
					subTitle: `Now tell us about ${this.userType === UserTypeE.Client ? "their" : "your"} activity levels.`,
					order: 11,
				};

			case 12:
				return {
					title: `What best describes ${this.userType === UserTypeE.Client ? "their" : "your"} weekly workouts?`,
					subTitle: "Cool. Let's go a little deeper on exercise.",
					order: 12,
				};
			case 13:
				return {
					title: `${this.userType === UserTypeE.Client ? "Their" : "Your"} Results`,
					subTitle: "",
					order: 13,
				};
			default:
				return {
					title: "Invalid Question Number",
					subTitle: "",
					order: -1,
				};
		}
	}

	calculateNutrition(): void {
		this.rmr = this.calculateRmr();
		this.rmrActivity = this.calculateRmrActivity(this.rmr);
		this.rmrActivityGoal = this.calculateRmrGoal(this.rmrActivity);

		this.caloriesProtein =
			(this.rmrActivityGoal * this.goal.macroNutrient!.macroRatio.protein) /
			100;
		this.caloriesVeg = this.detail.gender === GenderE.Female ? 125 : 175;
		this.caloriesCarb =
			((this.rmrActivityGoal * this.goal.macroNutrient!.macroRatio.carbs) / 100) - this.caloriesVeg;
		this.caloriesFat =
			(this.rmrActivityGoal * this.goal.macroNutrient!.macroRatio.fats) / 100;

		this.gramsProtein = this.caloriesProtein / 4;
		this.gramsCarb = this.caloriesCarb / 4;

		this.gramsVeg = this.caloriesVeg / 3;

		this.gramsPerMealProtein = Math.floor(
			this.gramsProtein / this.goal.mealCount!
		);
		this.gramsPerMealCarb = Math.floor(this.gramsCarb / this.goal.mealCount!);
		this.gramsPerMealVeg = Math.floor(this.gramsVeg / this.goal.mealCount!);

		this.palmsProtein = Math.floor(
			this.calculatePalmProtein(this.gramsProtein)
		);
		this.cuppedHandCarb = Math.floor(this.calculateCuppedCarb(this.gramsCarb));
		this.fistVegies = this.detail.gender === GenderE.Female ? 5 : 7;

		this.gramsFat = (this.caloriesFat / 9) - (this.palmsProtein * 4) - this.cuppedHandCarb;
		this.gramsPerMealFat = Math.floor(this.gramsFat / this.goal.mealCount!);
		this.thumbsFat = Math.floor(this.calculateThumbFat(this.gramsFat));
	}

	getProteinPalmText(): string {
		return this.getNutriText(this.palmsProtein, "total palms");
	}

	getCarbHandText(): string {
		return this.getNutriText(this.cuppedHandCarb, "total handfuls");
	}

	getFatThumText(): string {
		return this.getNutriText(this.thumbsFat, "total thumbs");
	}

	getVegFistText(): string {
		return this.getNutriText(this.fistVegies, "total fists");
	}

	getVegFistTotalText(): string {
		return this.detail.gender === GenderE.Female ? "4-6" : "6-8";
	}

	getNutriText(value: number, text: string): string {
		const numberPerMeal = value / this.goal.mealCount!;
		if (value % this.goal.mealCount! === 0) {
			return numberPerMeal + " " + text;
		} else {
			return (
				Math.floor(numberPerMeal) +
				" or " +
				Math.ceil(numberPerMeal) +
				" " +
				text
			);
		}
	}

	calculateRmr(): number {
		const weight = this.detail.weight!.weightKg!;
		const height = this.detail.height!.heightCm!;
		const age = this.detail.age!;
		if (this.detail.gender === GenderE.Female) {
			return 10 * weight + 6.25 * height - 5 * age - 161;
		} else {
			return 10 * weight + 6.25 * height - 5 * age + 5;
		}
	}

	calculateRmrActivity(rmr: number): number {
		let activityMutlipler = this.goal.getActivityMultiplier();
		return rmr * activityMutlipler;
	}

	calculateRmrGoal(rmrActivity: number): number {
		let goalAdjustment = this.goal.getBodyGoalAdjustment();
		return rmrActivity * ((100 - goalAdjustment) / 100);
	}

	calculatePalmProtein(grams: number): number {
		if (this.detail.gender === GenderE.Female) {
			return grams / 22;
		} else {
			return grams / 24;
		}
	}

	calculateCuppedCarb(grams: number): number {
		if (this.detail.gender === GenderE.Female) {
			return grams / 22;
		} else {
			return grams / 25;
		}
	}

	calculateThumbFat(grams: number): number {
		if (this.detail.gender === GenderE.Female) {
			return grams / 8;
		} else {
			return grams / 9;
		}
	}

	createMock() {
		this.detail.setAge(35);
		this.detail.setWeight({ weightKg: 75, selectedUnit: WeightUnits.kg });
		this.detail.setHeight({ heightCm: 180, selectedUnit: HeightUnits.cm });
		this.detail.setGender(GenderE.Male);

		this.goal.setReachGoal(ReachGoal.BodyRecomp);
		this.goal.setTargetWeight({
			weightLbs: 185,
			selectedUnit: WeightUnits.lbs,
		});
		this.goal.setTargetDate("2021-12-31");
		this.goal.setEatingStyle(Diet.Mediterranean);
		this.goal.macroNutrient = new Macronutrient(
			Util.getMacroNutrientRatio(Diet.Mediterranean),
			GraphMode.standard
		);
		this.goal.setMealCount(3);
		this.goal.setActivity(Activity.Moderate);
		this.goal.setWorkout(Workout.Moderate);

		this.calculateNutrition();
	}
}
